import React, { useState, useEffect } from 'react';
import { X, MapPin, Truck, Clock, CircleCheck as CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

interface RouteStop {
  id: string;
  client_name: string;
  address: string;
  amount: number;
  status: string;
}

interface CircuitRoute {
  id: string;
  circuit_plan_id: string;
  title: string;
  route_date: string;
  ruta: number | null;
  driver_name: string;
  status: string;
  stops_count: number;
  created_at: string;
  stops?: RouteStop[];
}

interface Props {
  routeDate?: string;
  onClose: () => void;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

export default function CircuitRoutesPanel({ routeDate, onClose }: Props) {
  const { user } = useAuth();
  const [routes, setRoutes] = useState<CircuitRoute[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedRoute, setExpandedRoute] = useState<string | null>(null);

  useEffect(() => {
    fetchRoutes();
  }, [routeDate]);

  const fetchRoutes = async () => {
    if (!user?.unit_id) return;
    setLoading(true);
    try {
      let query = supabase
        .from('circuit_routes')
        .select(`
          id,
          circuit_plan_id,
          title,
          route_date,
          ruta,
          driver_name,
          status,
          stops_count,
          created_at,
          circuit_route_stops(id, client_name, address, amount, status)
        `)
        .eq('unit_id', user.unit_id)
        .order('created_at', { ascending: false });

      if (routeDate) {
        query = query.eq('route_date', routeDate);
      } else {
        query = query.limit(20);
      }

      const { data, error } = await query;
      if (error) throw error;

      setRoutes(
        (data || []).map((r: any) => ({
          ...r,
          stops: r.circuit_route_stops || [],
        }))
      );
    } catch (err) {
      console.error('Error fetching circuit routes:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (routeId: string) => {
    setExpandedRoute(prev => (prev === routeId ? null : routeId));
  };

  const statusConfig: Record<string, { label: string; color: string }> = {
    created: { label: 'Criada', color: 'bg-gray-100 text-gray-700' },
    optimized: { label: 'Otimizada', color: 'bg-blue-100 text-blue-700' },
    distributed: { label: 'Enviada', color: 'bg-green-100 text-green-700' },
    completed: { label: 'Concluida', color: 'bg-emerald-100 text-emerald-700' },
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-lg max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Truck className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-800">Rotas do Circuit</h2>
              {routeDate && (
                <p className="text-sm text-gray-500">
                  {new Date(routeDate + 'T00:00:00').toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })}
                </p>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : routes.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <MapPin className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">Nenhuma rota criada</p>
              <p className="text-sm mt-1">Selecione clientes na agenda e crie uma rota no Circuit.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {routes.map(route => {
                const isExpanded = expandedRoute === route.id;
                const cfg = statusConfig[route.status] || statusConfig.created;

                return (
                  <div key={route.id} className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleExpand(route.id)}
                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-3 text-left">
                        <MapPin className="w-4 h-4 text-blue-500 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-gray-800">{route.title}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-gray-500 flex items-center gap-1">
                              <Truck className="w-3 h-3" />
                              {route.driver_name}
                            </span>
                            <span className="text-xs text-gray-400">|</span>
                            <span className="text-xs text-gray-500">
                              {route.stops_count} parada{route.stops_count !== 1 ? 's' : ''}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cfg.color}`}>
                              {cfg.label}
                            </span>
                          </div>
                        </div>
                      </div>
                      {isExpanded ? (
                        <ChevronUp className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      )}
                    </button>

                    {isExpanded && route.stops && route.stops.length > 0 && (
                      <div className="border-t divide-y divide-gray-100 bg-gray-50">
                        {route.stops.map(stop => (
                          <div key={stop.id} className="px-4 py-2.5 flex justify-between items-center">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-gray-800">{stop.client_name}</p>
                              <p className="text-xs text-gray-500 truncate">{stop.address}</p>
                            </div>
                            <span className="text-sm font-medium text-green-700 ml-3">
                              {formatCurrency(stop.amount)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
